import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/apiClient';
import { useAuth } from './AuthContext';
import { setGlobalTz } from '../lib/globalTz';

export interface Customer {
  id: string;
  name: string;
  color?: string;
  logoUrl?: string;
  logoFileName?: string;
  logoUpdatedAt?: string | null;
  enabled: boolean;
  isDefault?: boolean;
  hasSecret?: boolean;
  /** IANA timezone (e.g. "Asia/Kuala_Lumpur"). Falls back to UTC if missing. */
  timezone?: string;
  /**
   * Domains monitored by the Dark-Web Scan tab. Always an array on the wire
   * (default `[]`); the field is optional only because older clients may
   * not have refreshed yet.
   */
  monitoredDomains?: string[];
  /**
   * Autonomous Sentinel-incident triage agent mode (per-customer opt-in).
   * The API always returns these on `GET /customers`; declared optional
   * here only because older cached clients may receive a payload from a
   * server build that predated the column.
   */
  triageMode?: 'off' | 'recommend' | 'auto_close_high_confidence';
  triageAutoCloseMinConfidence?: number;
  subscription?: string;
  resourceGroup?: string;
  workspaceId?: string;
  workspaceName?: string;
  tenantId?: string;
  lighthouseMode?: boolean;
}

interface CustomerContextValue {
  customers: Customer[];
  activeCustomerId: string;
  activeCustomer: Customer | undefined;
  setActiveCustomerId: (id: string) => void;
  refresh: () => Promise<void>;
  isLoading: boolean;
}

const CustomerContext = createContext<CustomerContextValue | null>(null);

/** Shared cache key - exported so any other component can invalidate the
 *  customer list (e.g. after the SettingsPanel adds/edits a customer). */
export const CUSTOMERS_QUERY_KEY = ['customers'] as const;

const POLL_INTERVAL_MS = 30_000; // background refresh cadence

export function CustomerProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // For SOC analysts (any tier - soc_l1/soc_l2/legacy analyst), seed
  // activeCustomerId with their first allowed customer so the dashboard
  // doesn't briefly show 'default' (which they can't access). Admins start
  // from 'default' and switch to the first real customer when the list arrives.
  const isScopedSocStaff =
    user?.role === 'soc_l1' || user?.role === 'soc_l2' || user?.role === 'analyst' || user?.role === 'soc_manager';
  const initialActive = isScopedSocStaff && user?.allowedCustomerIds[0]
    ? user.allowedCustomerIds[0]
    : 'default';
  const [activeCustomerId, setActiveCustomerIdState] = useState<string>(initialActive);
  const activeRef = useRef(activeCustomerId);
  activeRef.current = activeCustomerId;

  // Single source of truth for the customer list. React Query handles
  // dedupe (so the SettingsPanel + nav header don't double-fetch) and
  // automatically pauses background polling when the tab is hidden.
  const customersQuery = useQuery({
    queryKey: CUSTOMERS_QUERY_KEY,
    queryFn: async () => {
      const data = await apiFetch<{ customers: Customer[] }>('/customers');
      // Array.isArray, not just `?? []` - a non-array truthy response (e.g.
      // an error payload shaped differently than expected) must not reach
      // .some()/.find() below, which would throw "X.some is not a function"
      // and crash the whole app since this provider wraps every tab.
      return Array.isArray(data.customers) ? data.customers : [];
    },
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
  const customers: Customer[] = Array.isArray(customersQuery.data) ? customersQuery.data : [];
  const isLoading = customersQuery.isLoading;

  // Self-heal active customer: if the server tells us the current id no
  // longer exists, switch to the first available; if we're still on the
  // bootstrap 'default' placeholder, switch as soon as a real list arrives.
  useEffect(() => {
    if (customers.length === 0) return;
    const currentId = activeRef.current;
    const stillExists = customers.some((c) => c.id === currentId);
    if (!stillExists || currentId === 'default') {
      setActiveCustomerIdState(customers[0].id);
      activeRef.current = customers[0].id;
    }
  }, [customers]);

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: CUSTOMERS_QUERY_KEY });
  }, [queryClient]);

  function setActiveCustomerId(id: string) {
    setActiveCustomerIdState(id);
    activeRef.current = id;
  }

  const activeCustomer = customers.find((c) => c.id === activeCustomerId);

  // Keep the global tz formatter in sync so all components display times correctly.
  useEffect(() => { setGlobalTz(activeCustomer?.timezone ?? 'UTC'); }, [activeCustomer]);

  return (
    <CustomerContext.Provider value={{ customers, activeCustomerId, activeCustomer, setActiveCustomerId, refresh, isLoading }}>
      {children}
    </CustomerContext.Provider>
  );
}

export function useCustomers(): CustomerContextValue {
  const ctx = useContext(CustomerContext);
  if (!ctx) throw new Error('useCustomers must be used inside <CustomerProvider>');
  return ctx;
}
